import { teamTypeLabel } from './teamTypes';

/**
 * The user form's team memberships: every team (and installer partner) a person belongs to,
 * shown as a list.
 *
 * Saving a user replaces ALL of their memberships with the ids the form sends
 * (update-admin-user deletes user_teams and user_installer_partners, then re-inserts). So the
 * list must carry every membership through, including any it cannot name, or saving silently
 * drops it.
 */
export const INSTALLER_PARTNER = 'INSTALLER_PARTNER';
/** A partner company of type EPC. Stored like an installer partner (user_installer_partners). */
export const EPC_PARTNER = 'EPC_PARTNER';

export interface Membership {
  kind: 'team' | 'installer';
  id: string;
  name: string;
  /** A team_type value, EPC_PARTNER or INSTALLER_PARTNER. */
  type: string;
}

interface TeamLike {
  id: string | number;
  name: string;
  type: string;
}
interface PartnerLike {
  id: string;
  name: string;
  /** installer_partners.partner_type; absent means an installer. */
  partnerType?: string | null;
}

const partnerMembershipType = (p: PartnerLike) => (p.partnerType === 'EPC' ? EPC_PARTNER : INSTALLER_PARTNER);

/** The order types are listed in, both in the form and in the "add" picker. */
const TYPE_ORDER = ['SALES', 'EPC', EPC_PARTNER, 'INTERNAL_PM', 'BIZ_DEV', 'INSTALLER', INSTALLER_PARTNER];
const typeRank = (t: string) => {
  const i = TYPE_ORDER.indexOf(t);
  return i === -1 ? TYPE_ORDER.length : i;
};
const byTypeThenName = (a: Membership, b: Membership) =>
  a.type === b.type ? a.name.localeCompare(b.name) : typeRank(a.type) - typeRank(b.type);

export function membershipTypeLabel(type: string): string {
  if (type === INSTALLER_PARTNER) return 'Installer partner';
  if (type === EPC_PARTNER) return 'EPC';
  return teamTypeLabel(type);
}

export function membershipsFromUser(input: {
  userTeamIds: string[];
  installerPartnerIds: string[];
  teams: TeamLike[];
  partners: PartnerLike[];
}): { visible: Membership[]; hidden: string[] } {
  const visible: Membership[] = [];
  const hidden: string[] = [];
  const seen = new Set<string>();
  for (const raw of input.userTeamIds) {
    const id = String(raw);
    if (seen.has(id)) continue;
    seen.add(id);
    const team = input.teams.find((t) => String(t.id) === id);
    if (team) visible.push({ kind: 'team', id, name: team.name, type: team.type });
    else hidden.push(id);
  }
  for (const raw of input.installerPartnerIds) {
    const id = String(raw);
    if (seen.has(id)) continue;
    seen.add(id);
    const partner = input.partners.find((p) => p.id === id);
    if (partner) visible.push({ kind: 'installer', id, name: partner.name, type: partnerMembershipType(partner) });
    else hidden.push(id);
  }
  return { visible: visible.sort(byTypeThenName), hidden };
}

/** The `team_ids` the user endpoints expect: team ids and installer partner UUIDs together. */
export function membershipsToTeamIds(visible: Membership[], hidden: string[]): string[] {
  return [...new Set([...visible.map((m) => m.id), ...hidden])];
}

function allOptions(teams: TeamLike[], partners: PartnerLike[]): Membership[] {
  return [
    ...teams.map((t) => ({ kind: 'team' as const, id: String(t.id), name: t.name, type: t.type })),
    ...partners.map((p) => ({ kind: 'installer' as const, id: p.id, name: p.name, type: partnerMembershipType(p) })),
  ];
}

export function addableOptions(
  type: string,
  teams: TeamLike[],
  partners: PartnerLike[],
  current: Membership[]
): Membership[] {
  const taken = new Set(current.map((m) => m.id));
  return allOptions(teams, partners)
    .filter((o) => o.type === type && !taken.has(o.id))
    .sort(byTypeThenName);
}

export function addableMembershipTypes(
  teams: TeamLike[],
  partners: PartnerLike[],
  current: Membership[]
): { value: string; label: string }[] {
  const taken = new Set(current.map((m) => m.id));
  const types = new Set(allOptions(teams, partners).filter((o) => !taken.has(o.id)).map((o) => o.type));
  return [...types].sort((a, b) => typeRank(a) - typeRank(b)).map((value) => ({ value, label: membershipTypeLabel(value) }));
}

export function addMembership(current: Membership[], m: Membership): Membership[] {
  return current.some((c) => c.id === m.id) ? current : [...current, m].sort(byTypeThenName);
}
